import { AppState, AppThunkAction } from '../reducers';

// services
import * as Services from '../../services/PersonIdentifierTypes.Services';

// actions
import * as Actions from '../actions';
import * as ActionEvents from '../actions/PersonIdentifierTypes.Actions';

// models
import { ErrorType } from '../../models/HttpError';
import {
    IdentifierTypeApiError,
    IdentifierTypeViewModel,
    IdentifierTypeCreateCommand,
    IdentifierTypeUpdateCommand
} from '../../models/Entities/Person/IdentifierType/IdentifierTypeModels';

export type KnownAction =
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeGetAllRequestAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeGetAllSuccessAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeGetAllFailureAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeGetByIdRequestAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeGetByIdSuccessAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeGetByIdFailureAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeCreateRequestAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeCreateSuccessAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeCreateFailureAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeUpdateRequestAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeUpdateSuccessAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeUpdateFailureAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeDeleteRequestAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeDeleteSuccessAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeDeleteFailureAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeStatusRequestAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeStatusSuccessAction
    | Actions.PersonIdentifierTypes.PersonIdentifierTypeStatusFailureAction;

const NotAuthorizedError: ErrorType = {
    ErrorCode: 401,
    ErrorMessage: 'Not authorized',
    Errors: []
};

/** Thunk que devuelve promesa (spec 011): la pantalla sabe el resultado del alta, la modificación o la baja. */
export type IdentifierTypesThunk<TResult> = (dispatch: (action: KnownAction) => void, getState: () => AppState) => Promise<TResult>;

const tokenOf = (getState: () => AppState): string | null => getState()?.oidc?.user?.access_token ?? null;

const toErrorType = (error: unknown): ErrorType => {
    if (typeof error === 'object' && error !== null && 'ErrorCode' in error) return error as ErrorType;
    return { ErrorCode: 0, ErrorMessage: error instanceof Error ? error.message : 'Unexpected error', Errors: [] };
};

export const actionCreators = {
    GetIdentifierTypes: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_GETALL_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_GETALL_REQUEST });

        Services.GetIdentifierTypes(token)
            .then((response: IdentifierTypeViewModel[]) => {
                dispatch({
                    type: ActionEvents.PERSON_IDENTIFIER_TYPE_GETALL_SUCCESS,
                    payload: response
                });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_GETALL_FAILURE, error });
            });
    },

    GetIdentifierTypeById: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_GETBYID_FAILURE, error: NotAuthorizedError });
            return;
        }

        dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_GETBYID_REQUEST, id });

        Services.GetIdentifierTypeById(token, id)
            .then((response: IdentifierTypeViewModel) => {
                dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_GETBYID_SUCCESS, payload: response });
            })
            .catch((error: ErrorType) => {
                dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_GETBYID_FAILURE, error });
            });
    },

    /** Alta. Resuelve con el tipo creado; rechaza con el `ErrorType` (el estado también queda en el store). */
    CreateIdentifierType: (payload: IdentifierTypeCreateCommand): IdentifierTypesThunk<IdentifierTypeViewModel> => async (dispatch, getState) => {
        const token = tokenOf(getState);

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_CREATE_FAILURE, error: NotAuthorizedError });
            throw NotAuthorizedError;
        }

        dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_CREATE_REQUEST, payload });

        try {
            const created: IdentifierTypeViewModel = await Services.CreateIdentifierType(token, payload);
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_CREATE_SUCCESS, payload: created });
            return created;
        } catch (error) {
            const typed = toErrorType(error);
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_CREATE_FAILURE, error: typed });
            throw typed;
        }
    },

    /** Modificación (también activar / desactivar). Resuelve con el tipo actualizado; rechaza con el `ErrorType`. */
    UpdateIdentifierType: (payload: IdentifierTypeUpdateCommand): IdentifierTypesThunk<IdentifierTypeViewModel> => async (dispatch, getState) => {
        const token = tokenOf(getState);

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_UPDATE_FAILURE, id: payload.id, error: NotAuthorizedError });
            throw NotAuthorizedError;
        }

        dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_UPDATE_REQUEST, payload });

        try {
            const updated: IdentifierTypeViewModel = await Services.UpdateIdentifierType(token, payload);
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_UPDATE_SUCCESS, payload: updated });
            return updated;
        } catch (error) {
            const typed = toErrorType(error);
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_UPDATE_FAILURE, id: payload.id, error: typed });
            throw typed;
        }
    },

    /**
     * Activar / desactivar con `PATCH persons/identifierTypes/{id}/status` (Persons.API spec 005). Resuelve con el
     * tipo que devuelve el servidor (y lo reemplaza en el store); rechaza con el error.
     *
     * Respaldo para Persons.API anterior a la spec 005 (la ruta no existe: 404 sin envoltura o 405): si se pasa
     * `legacyCommand` (el tipo completo con `isActive` cambiado) se reintenta con el `PUT` de siempre y se avisa
     * por consola. Un 404 con envoltura es "el tipo no existe" y no reintenta.
     */
    ChangeIdentifierTypeStatus: (
        id: number,
        isActive: boolean,
        legacyCommand?: IdentifierTypeUpdateCommand
    ): IdentifierTypesThunk<IdentifierTypeViewModel> => async (dispatch, getState) => {
        const token = tokenOf(getState);

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_STATUS_FAILURE, id, error: NotAuthorizedError });
            throw NotAuthorizedError;
        }

        dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_STATUS_REQUEST, id, isActive });

        try {
            let updated: IdentifierTypeViewModel;
            try {
                updated = await Services.ChangeIdentifierTypeStatus(token, id, isActive);
            } catch (error) {
                if (!legacyCommand || !(error as IdentifierTypeApiError | undefined)?.RouteMissing) throw error;
                console.warn(
                    'PATCH persons/identifierTypes/{id}/status no disponible (Persons.API anterior a la spec 005): se usa PUT.',
                    error
                );
                updated = await Services.UpdateIdentifierType(token, { ...legacyCommand, id, isActive });
            }
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_STATUS_SUCCESS, payload: updated });
            return updated;
        } catch (error) {
            const typed = toErrorType(error);
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_STATUS_FAILURE, id, error: typed });
            throw typed;
        }
    },

    /** Baja (lógica en el servidor). Resuelve con el id; rechaza con el `ErrorType`. */
    DeleteIdentifierType: (id: number): IdentifierTypesThunk<number> => async (dispatch, getState) => {
        const token = tokenOf(getState);

        if (!token) {
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_DELETE_FAILURE, id, error: NotAuthorizedError });
            throw NotAuthorizedError;
        }

        dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_DELETE_REQUEST, id });

        try {
            await Services.DeleteIdentifierType(token, id);
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_DELETE_SUCCESS, id });
            return id;
        } catch (error) {
            const typed = toErrorType(error);
            dispatch({ type: ActionEvents.PERSON_IDENTIFIER_TYPE_DELETE_FAILURE, id, error: typed });
            throw typed;
        }
    }
};
